import React, { useEffect, useState, useCallback } from "react";
import { useOutletsQuery } from "../../../Store/Apis/outletApi";
import { useSalesReportQuery } from "../../../Store/Apis/reportApi";
import Loader from "../../../Components/Loader/Loader";
import Error from "../../../Components/Error/Error";
import downloadPDF from "../../../Hooks/downloadReportPdf";
import { Link } from "react-router-dom";
import { isDropdownPermission } from "../../../Hooks/permissionsMiddleware";
import { useSelector } from "react-redux";
import formatDate from "../../../Hooks/formatDate";
import {
  usePaymentTypesQuery,
  useStatusQuery,
} from "../../../Store/Apis/bookingApi";
import { FaRegFilePdf, FaFileExcel, FaSearch } from "react-icons/fa";
import * as XLSX from "xlsx";

// Helper function to get formatted date (YYYY-MM-DD)
const getFormattedDate = (date) => {
  return date.toISOString().split("T")[0];
};

const Sales = () => {
  const user = useSelector((state) => state.auth.user);
  const today = new Date();
  const yesterday = new Date(today);
  yesterday.setDate(today.getDate() - 1);

  const [startDate, setStartDate] = useState(getFormattedDate(yesterday));
  const [endDate, setEndDate] = useState(getFormattedDate(today));
  const [keyID, setKeyID] = useState("");
  const [searchTerm, setSearchTerm] = useState("");
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState("");
  const [filter, setFilter] = useState({
    paymentType: "",
    customerName: "",
    orderStatus: "",
  });

  // Debounce search term
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchTerm(searchTerm);
    }, 300);

    return () => clearTimeout(timer);
  }, [searchTerm]);

  // Handle search submit on Enter key
  const handleSearchSubmit = useCallback(
    (e) => {
      if (e.key === "Enter") {
        setDebouncedSearchTerm(searchTerm);
      }
    },
    [searchTerm],
  );

  // Clear search
  const handleClearSearch = useCallback(() => {
    setSearchTerm("");
    setDebouncedSearchTerm("");
  }, []);
  const outletList = useOutletsQuery();
  const paymentTypes = usePaymentTypesQuery();
  const statusApi = useStatusQuery();

  const {
    data: Report,
    isSuccess,
    isLoading,
    isFetching,
    isError,
  } = useSalesReportQuery(
    {
      startDate,
      endDate,
      keyID,
      paymentType: filter.paymentType,
      orderStatus: filter.orderStatus,
      // Don't send customerName to API, handle client-side
    },
    { skip: !startDate },
  );

  // Client-side filtering for customer search
  const filteredSales = React.useMemo(() => {
    if (!Report?.data?.salesDetails) return [];

    return Report.data.salesDetails.filter((sale) => {
      // Customer name search - case insensitive, trim whitespace
      let customerMatch = true;
      if (debouncedSearchTerm) {
        const searchTerm = debouncedSearchTerm.toLowerCase().trim();
        const customerName = (sale.customer || "").toLowerCase().trim();
        customerMatch = customerName.includes(searchTerm);
      }

      // Payment type filter
      const paymentMatch =
        !filter.paymentType ||
        sale.paymentDetails?.some(
          (payment) => payment.paymentTypeId === parseInt(filter.paymentType),
        );

      // Status filter
      const statusMatch =
        !filter.orderStatus || sale.statusId === parseInt(filter.orderStatus);

      return customerMatch && paymentMatch && statusMatch;
    });
  }, [
    Report?.data?.salesDetails,
    debouncedSearchTerm,
    filter.paymentType,
    filter.orderStatus,
  ]);

  // Calculate Total Order Amount for filtered data
  const filteredTotalSaleAmount = filteredSales
    .reduce((sum, order) => sum + order.total, 0)
    .toFixed(2);

  // PAGINATION CODE HERE
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 10;
  const handlePageChange = (pageNumber) => {
    setCurrentPage(pageNumber);
  };

  const totalPages =
    filteredSales.length > 0
      ? Math.ceil(filteredSales.length / itemsPerPage)
      : 0;
  const paginatedData = filteredSales.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage,
  );

  // Reset to first page when filters change
  useEffect(() => {
    setCurrentPage(1);
  }, [debouncedSearchTerm, filter.paymentType, filter.orderStatus]);

  const renderPagination = () => {
    const pages = [];
    const pageLimit = 5;
    const startPage = Math.max(2, currentPage - 2);
    const endPage = Math.min(totalPages - 1, currentPage + 2);

    if (totalPages > 1) {
      pages.push(
        <li
          key={1}
          className={`page-item ${1 === currentPage ? "active" : ""}`}
        >
          <button className="page-link" onClick={() => handlePageChange(1)}>
            1
          </button>
        </li>,
      );

      if (startPage > 2) {
        pages.push(
          <li key="start-ellipsis" className="page-item disabled">
            <span className="page-link">...</span>
          </li>,
        );
      }

      for (let i = startPage; i <= endPage; i++) {
        pages.push(
          <li
            key={i}
            className={`page-item ${i === currentPage ? "active" : ""}`}
          >
            <button className="page-link" onClick={() => handlePageChange(i)}>
              {i}
            </button>
          </li>,
        );
      }

      if (endPage < totalPages - 1) {
        pages.push(
          <li key="end-ellipsis" className="page-item disabled">
            <span className="page-link">...</span>
          </li>,
        );
      }

      pages.push(
        <li
          key={totalPages}
          className={`page-item ${totalPages === currentPage ? "active" : ""}`}
        >
          <button
            className="page-link"
            onClick={() => handlePageChange(totalPages)}
          >
            {totalPages}
          </button>
        </li>,
      );
    }

    return pages;
  };

  // GENERATE PDF CODE
  const outlet =
    keyID &&
    outletList?.data?.data.find((elem) => elem.outletId === Number(keyID));
  const outletName = outlet?.outletName || "";
  const rows =
    Array.isArray(Report?.data.salesDetails) &&
    Report?.data?.salesDetails.map((item, index) => [
      index + 1,
      formatDate(item.date),
      item.orderNumber,
      item.customer,
      `R ${item.total.toFixed(2)}`,
      `R ${item.dueAmount.toFixed(2)}`,
      item.paymentDetails
        .map((elem) => {
          if (elem.paymentMode !== "") {
            return `${elem.payAmount.toFixed(2)} - ${elem.paymentMode}`;
          }
        })
        .join("\n"),
    ]);
  const columns = [
    "Sl No.",
    "Date",
    "Order ID",
    "Customer",
    "Total",
    "Due Amount",
    "Payment Mode",
  ];

  useEffect(() => {
    if (!isDropdownPermission(user.role_Id)) {
      setKeyID(user.outletID);
    }
  }, []);

  // Excel Download Function
  const downloadExcel = () => {
    if (
      !Report?.data?.salesDetails ||
      !Array.isArray(Report.data.salesDetails)
    ) {
      alert("No data available for download");
      return;
    }

    // Prepare data for Excel
    const excelData = Report.data.salesDetails.map((item, index) => ({
      "Sl No.": index + 1,
      Date: formatDate(item.date),
      "Order ID": item.orderNumber || "",
      Customer: item.customer || "N/A",
      Total: item.total,
      "Due Amount": item.dueAmount,
      "Payment Mode": item.paymentDetails
        .map((elem) => {
          if (elem.paymentMode !== "") {
            return `${elem.payAmount.toFixed(2)} - ${elem.paymentMode}`;
          }
          return "";
        })
        .filter(Boolean)
        .join(", "),
    }));

    // Create workbook and worksheet
    const workbook = XLSX.utils.book_new();
    const worksheet = XLSX.utils.json_to_sheet(excelData);

    // Set column widths for better formatting
    const columnWidths = [
      { wch: 8 }, // Sl No.
      { wch: 12 }, // Date
      { wch: 15 }, // Order ID
      { wch: 20 }, // Customer
      { wch: 12 }, // Total
      { wch: 12 }, // Due Amount
      { wch: 30 }, // Payment Mode
    ];
    worksheet["!cols"] = columnWidths;

    // Add worksheet to workbook
    XLSX.utils.book_append_sheet(workbook, worksheet, "Sales Report");

    // Generate filename with date range
    const filename = `Sales_Report_${formatDate(startDate)}-${formatDate(
      endDate,
    )}.xlsx`;

    // Download the file
    XLSX.writeFile(workbook, filename);
  };

  return (
    <div>
      <div className="row align-items-center justify-content-between mb-4">
        <div className="col">
          <h5 className="fw-500 text-white">Sales Report</h5>
        </div>
      </div>
      <div className="card mb-4">
        <div className="card-header p-4">
          <div className="row gy-2">
            <div className="col-md-4">
              <label>Start Date</label>
              <input
                type="date"
                className="form-control"
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
              />
            </div>
            <div className="col-md-4">
              <label>End Date</label>
              <input
                type="date"
                className="form-control"
                value={endDate}
                onChange={(e) => setEndDate(e.target.value)}
              />
            </div>
            <div className="col-md-4">
              <label>Customer/Company</label>
              <div className="position-relative">
                <input
                  name="customerName"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  onKeyDown={handleSearchSubmit}
                  className="form-control pe-5"
                  placeholder="Enter Customer Name"
                />
                <FaSearch
                  className="position-absolute top-50 translate-middle-y text-muted"
                  style={{ right: "15px", fontSize: "14px" }}
                />
              </div>
              {debouncedSearchTerm && (
                <small className="text-primary mt-1 d-block">
                  Searching for: "{debouncedSearchTerm}" ({filteredSales.length}{" "}
                  matches)
                </small>
              )}
            </div>
            <div className="col-md-4">
              <label>Payment Types</label>
              <select
                className="form-select"
                value={filter?.payment}
                onChange={(e) =>
                  setFilter({
                    ...filter,
                    paymentType: e.target.value,
                  })
                }
                disabled={!paymentTypes.isSuccess}
              >
                <option value="">Select Payment</option>
                {paymentTypes?.data?.data.map((elem) => (
                  <option key={elem.id} value={elem.id}>
                    {elem.paymentType}
                  </option>
                ))}
              </select>
            </div>
            <div className="col-md-4">
              <label>Status</label>
              <select
                className="form-select"
                value={filter?.orderStatus}
                onChange={(e) =>
                  setFilter({
                    ...filter,
                    orderStatus: e.target.value,
                  })
                }
                disabled={!statusApi.isSuccess}
              >
                <option value="">Select Status</option>
                {statusApi?.data?.data.map((elem) => (
                  <option key={elem.id} value={elem.id}>
                    {elem.description}
                  </option>
                ))}
              </select>
            </div>
            <div className="col-md-4">
              <label>Outlet name</label>
              <select
                className="form-select"
                value={keyID}
                onChange={(e) => setKeyID(e.target.value)}
                disabled={
                  !outletList.isSuccess || !isDropdownPermission(user.role_Id)
                }
              >
                <option value="">All Outlets</option>
                {outletList?.data?.data.map((elem) => (
                  <option key={elem.outletId} value={elem.outletId}>
                    {elem.outletName}
                  </option>
                ))}
              </select>
            </div>
          </div>
        </div>
        <div className="card-body p-0">
          {isError && <Error />}
          {(isLoading || isFetching) && <Loader height="300px" />}
          {!isLoading && !isFetching && (
            <>
              <div className="table-responsive mb-4">
                <table className="table table-bordered align-items-center mb-0">
                  <thead className="bg-light">
                    <tr>
                      <th
                        style={{ width: "15%" }}
                        className="text-uppercase text-secondary text-xs opacity-7"
                      >
                        Date
                      </th>
                      <th
                        style={{ width: "10%" }}
                        className="text-uppercase text-secondary text-xs opacity-7"
                      >
                        Order ID
                      </th>
                      <th
                        style={{ width: "15%" }}
                        className="text-uppercase text-secondary text-xs  opacity-7"
                      >
                        Customer / Company
                      </th>
                      <th
                        style={{ width: "15%" }}
                        className="text-uppercase text-secondary text-xs  opacity-7"
                      >
                        Total
                      </th>
                      <th
                        style={{ width: "10%" }}
                        className="text-uppercase text-secondary text-xs  opacity-7"
                      >
                        Due Amount
                      </th>
                      <th
                        style={{ width: "10%" }}
                        className="text-uppercase text-secondary text-xs  opacity-7"
                      >
                        Payment Mode
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {paginatedData.length > 0 ? (
                      paginatedData.map((sale, index) => (
                        <tr key={index}>
                          <td className="text-sm px-4">
                            {formatDate(sale.date)}
                          </td>
                          <td className="text-sm px-4">{sale.orderNumber}</td>
                          <td className="text-sm px-4">{sale.customer}</td>
                          <td className="text-sm px-4">
                            R {sale.total.toFixed(2)}
                          </td>
                          <td className="text-sm px-4">
                            R {sale.dueAmount.toFixed(2)}
                          </td>
                          <td className="text-sm px-4">
                            {sale.paymentDetails.map((elem, i) => {
                              if (elem.paymentMode !== "") {
                                return (
                                  <p className="mb-0 text-sm">
                                    {elem.payAmount.toFixed(2)}{" "}
                                    <span className="text-xs">
                                      [{elem.paymentMode}]
                                    </span>
                                  </p>
                                );
                              }
                            })}
                          </td>
                        </tr>
                      ))
                    ) : (
                      <tr>
                        <td colSpan={6} className="text-center py-5">
                          {debouncedSearchTerm ? (
                            <>
                              No sales found for customer "{debouncedSearchTerm}
                              "
                              <br />
                              <small className="text-muted">
                                Try a different search term or{" "}
                                <button
                                  className="btn btn-link p-0 text-primary"
                                  onClick={handleClearSearch}
                                >
                                  clear search
                                </button>
                              </small>
                            </>
                          ) : (
                            "No Data Found !!"
                          )}
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
              <nav>
                <ul className="pagination justify-content-center">
                  {renderPagination()}
                </ul>
              </nav>
              <div className="row align-items-center px-4 mb-3">
                <div className="col-md-7">
                  <div className="row">
                    <div className="col-md-4">
                      <span className="text-sm mb-0 fw-500">Total Orders:</span>
                      <span className="text-sm text-dark ms-2 fw-600 mb-0">
                        {filteredSales.length}
                        {debouncedSearchTerm && (
                          <small className="text-muted ms-1">
                            (filtered from {Report?.data?.totalOrders})
                          </small>
                        )}
                      </span>
                    </div>
                    <div className="col-md-4">
                      <span className="text-sm mb-0 fw-500">Total Sales:</span>
                      <span className="text-sm text-dark ms-2 fw-600 mb-0">
                        R {filteredTotalSaleAmount}
                        {debouncedSearchTerm && (
                          <small className="text-muted ms-1">
                            (filtered from R{" "}
                            {Report?.data?.totalSales?.toFixed(2)})
                          </small>
                        )}
                      </span>
                    </div>
                    <div className="col-md-4">
                      <span className="text-sm mb-0 fw-500">
                        Total Tax Amount:
                      </span>
                      <span className="text-sm text-dark ms-2 fw-600 mb-0">
                        R {Report?.data?.totalTaxAmount.toFixed(2)}
                      </span>
                    </div>
                  </div>
                </div>
                <div className="col-md-5 text-end">
                  <div className="text-end px-4">
                    <button
                      className="btn btn-success me-2"
                      onClick={() =>
                        downloadPDF({
                          title: "Sales Report",
                          docName: `Sales_report ${formatDate(
                            startDate,
                          )}-${formatDate(endDate)}`,
                          rows,
                          columns,
                          startDate: formatDate(startDate),
                          endDate: formatDate(endDate),
                          outletName,
                          totalOrder: Report?.data.totalOrders,
                          totalSale: Report?.data.totalSales,
                          totalTaxAmount: Report?.data.totalTaxAmount,
                        })
                      }
                      disabled={
                        !isSuccess ||
                        isLoading ||
                        isFetching ||
                        !Array.isArray(Report?.data.salesDetails)
                      }
                    >
                      Download PDF <FaRegFilePdf size={20} />
                    </button>
                    <button
                      className="btn btn-warning me-2"
                      onClick={downloadExcel}
                      disabled={
                        !isSuccess ||
                        isLoading ||
                        isFetching ||
                        !Array.isArray(Report?.data.salesDetails)
                      }
                    >
                      Download Excel <FaFileExcel size={20} />
                    </button>
                    {/* <Link
                      to={`print?startDate=${startDate}&endDate=${endDate}&outlet=${keyID}`}
                      target="_blank"
                      disabled={!isSuccess || isLoading || isFetching || !Array.isArray(Report?.data.salesDetails)}
                    >
                      <button
                        rel="noopener noreferrer"
                        className="btn btn-warning"
                        disabled={!isSuccess || isLoading || isFetching || !Array.isArray(Report?.data.salesDetails)}
                      >Print Report</button>
                    </Link> */}
                  </div>
                </div>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default Sales;
